import { useCallback, useMemo, useRef } from 'react';

import { useClickOutside } from '../../../hooks/useClickOutside';
import { MultiSelectChips } from './MultiSelectChips';
import { MultiSelectOptions } from './MultiSelectOptions';

export interface MultiSelectLabels {
  /** Text inside the toggle button (e.g. "All stations" / "3 stations"). */
  toggle: string;
  toggleAria: string;
  searchPlaceholder: string;
  searchAria?: string;
  loading: string;
  empty: string;
  clearAll?: string;
}

interface MultiSelectProps<T> {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  options: T[];
  selected: T[];
  onChange: (items: T[]) => void;
  loading: boolean;
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  getSublabel?: (item: T) => string;
  /** Material symbol name shown in the toggle button. */
  icon: string;
  labels: MultiSelectLabels;
  showChips?: boolean;
  getChipRemoveAria?: (item: T) => string;
  focusSearchOnOpen?: boolean;
}

/**
 * Generic searchable multi-select dropdown (checkbox options, optional chips row).
 * Open/search state and option loading stay with the caller, so options can come
 * from any source. Reuses the `station-multiselect-*` CSS namespace. */
export const MultiSelect = <T,>({
  isOpen,
  onOpen,
  onClose,
  search,
  onSearchChange,
  options,
  selected,
  onChange,
  loading,
  getId,
  getLabel,
  getSublabel,
  icon,
  labels,
  showChips = false,
  getChipRemoveAria,
  focusSearchOnOpen = false,
}: MultiSelectProps<T>) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useClickOutside(dropdownRef, onClose, isOpen);

  const selectedIds = useMemo(() => new Set(selected.map(getId)), [selected, getId]);
  const isSelected = useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  const handleOpen = () => {
    onOpen();
    if (focusSearchOnOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleToggle = (item: T) => {
    if (selectedIds.has(getId(item))) {
      onChange(selected.filter((s) => getId(s) !== getId(item)));
    } else {
      onChange([...selected, item]);
    }
  };

  return (
    <div className="station-multiselect-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className={`station-multiselect-btn ${isOpen ? 'open' : ''}`}
        onClick={handleOpen}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={labels.toggleAria}
      >
        <span className="station-multiselect-icon" aria-hidden="true">
          <span className="material-symbols-outlined">{icon}</span>
        </span>
        <span className="station-multiselect-text">{labels.toggle}</span>
        <span className="station-multiselect-chevron" aria-hidden="true">
          <span className="material-symbols-outlined">expand_more</span>
        </span>
      </button>

      {isOpen && (
        <div className="station-multiselect-dropdown" role="listbox">
          <div className="station-multiselect-search">
            <span className="material-symbols-outlined" aria-hidden="true">search</span>
            <input
              ref={inputRef}
              type="text"
              placeholder={labels.searchPlaceholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="station-multiselect-input"
              aria-label={labels.searchAria}
            />
          </div>

          {showChips && (
            <MultiSelectChips
              selected={selected}
              getId={getId}
              getLabel={getLabel}
              getChipRemoveAria={getChipRemoveAria}
              clearAllText={labels.clearAll}
              onRemove={(item) => onChange(selected.filter((s) => getId(s) !== getId(item)))}
              onClearAll={() => onChange([])}
            />
          )}

          <div className="station-multiselect-options">
            <MultiSelectOptions
              options={options}
              loading={loading}
              loadingText={labels.loading}
              emptyText={labels.empty}
              getId={getId}
              getLabel={getLabel}
              getSublabel={getSublabel}
              isSelected={isSelected}
              onToggle={handleToggle}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
